import { useEffect, useRef, useState } from "react";

/**
 * AboutSlideshow
 * ---------------------------------------------------------------
 * Autoplay 4-image loop for the "What is Prophets?" left column.
 * Uses two absolutely-positioned layers that cross-swap so there
 * is never a layout shift and never a white/black flash.
 *
 * Transition: a sci-fi dematerialization — the outgoing image
 * fragments (mask-driven scanline sweep + slight blur + chromatic
 * shift), while the incoming image reconstructs from the opposite
 * mask. Combined duration ≈ 750ms so each image still has a
 * ~3.5s window of full visibility inside a 4s per-slide budget.
 *
 * Respects `prefers-reduced-motion`: falls back to a subtle
 * opacity crossfade with the same order and timing.
 * ---------------------------------------------------------------
 */

const IMAGES = [
  "/img/prophets/bleu1.webp",
  "/img/prophets/green1.webp",
  "/img/prophets/red1.webp",
  "/img/prophets/orange1.webp",
];

const SLIDE_MS = 4000;      // full-visibility hold per image
const TRANSITION_MS = 750;  // fragment + reconstruct duration

export function AboutSlideshow() {
  const [current, setCurrent] = useState(0);
  const [next, setNext] = useState(null); // index of the image reconstructing
  const [phase, setPhase] = useState("idle"); // "idle" | "transitioning"
  const reduceMotionRef = useRef(false);

  // Preload all 4 images once so slides never flash.
  useEffect(() => {
    IMAGES.forEach((src) => { const im = new Image(); im.src = src; });
  }, []);

  // Track reduced-motion preference.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    reduceMotionRef.current = mq.matches;
    const on = (e) => { reduceMotionRef.current = e.matches; };
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);

  // Idle → schedule the next transition.
  useEffect(() => {
    if (phase !== "idle") return undefined;
    const id = window.setTimeout(() => {
      const n = (current + 1) % IMAGES.length;
      setNext(n);
      setPhase("transitioning");
    }, SLIDE_MS);
    return () => clearTimeout(id);
  }, [phase, current]);

  // Transitioning → finish the transition after TRANSITION_MS.
  useEffect(() => {
    if (phase !== "transitioning" || next === null) return undefined;
    const id = window.setTimeout(() => {
      setCurrent(next);
      setNext(null);
      setPhase("idle");
    }, TRANSITION_MS);
    return () => clearTimeout(id);
  }, [phase, next]);

  const outSrc = IMAGES[current];
  const inSrc = next !== null ? IMAGES[next] : null;
  const cls = `about-slideshow ${phase === "transitioning" ? "is-transitioning" : ""} ${reduceMotionRef.current ? "is-reduced" : ""}`;

  return (
    <div className={cls} aria-hidden="true" data-testid="about-slideshow">
      {/* Layer A — outgoing (or steady current when idle) */}
      <div className="about-slideshow__layer about-slideshow__layer--out">
        <img
          src={outSrc}
          alt=""
          className="about-slideshow__img"
          loading="eager"
          decoding="async"
          draggable={false}
        />
      </div>
      {/* Layer B — incoming during transitions */}
      {inSrc && (
        <div className="about-slideshow__layer about-slideshow__layer--in">
          <img
            src={inSrc}
            alt=""
            className="about-slideshow__img"
            loading="eager"
            decoding="async"
            draggable={false}
          />
        </div>
      )}
      {/* Sci-fi scan-shard overlay — only visible mid-transition */}
      <span className="about-slideshow__shards" />
      {/* Soft black vignette fades the image into the section bg */}
      <span className="about-slideshow__vignette" />
    </div>
  );
}
